import * as amplitude from '@amplitude/analytics-browser';

import type {
  AnalyticsAccountRelationship,
  AnalyticsNavigationSource,
  AnalyticsNavigationSurface,
  AnalyticsOrgType,
} from '@/lib/analyticsContext';
import { isAdvisoryCtaPath } from '@/lib/sensitiveUrl';
import { isPublicTokenRoute } from '@/lib/publicTokenRoutes';

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);
const EXPLICIT_ENABLE_VALUES = new Set(['1', 'true', 'yes', 'on']);
const AMPLITUDE_ENABLED_ENVIRONMENTS = new Set(['staging', 'production']);

// ENT-3044: initialise the analytics-only browser SDK. We deliberately do NOT
// use `@amplitude/unified`'s `initAll()` — it also boots Session Replay,
// Experiment, and Engagement (Guides & Surveys), none of which this app uses.
// The Engagement plugin loads a remote CDN bundle that emits
// `Amplitude Logger [Warn]: Cannot unregister the default instance` on the
// sign-in identity transition. Options are the flat BrowserOptions shape
// (serverZone + autocapture at the top level), not the nested unified shape.
const AMPLITUDE_OPTIONS = {
  serverZone: 'EU',
  autocapture: true,
} as const;

let initialized = false;
let initPromise: Promise<void> | null = null;
let currentEventGroups: Partial<Record<AmplitudeGroupType, string>> = {};

type AnalyticsHierarchyProperties = {
  'active org id': string | null;
  'active org type': AnalyticsOrgType;
  'msp org id': string | null;
  'customer org id': string | null;
  'supplier org id': string | null;
  'supplier id': string | null;
  'user role': string | null;
  'account relationship': AnalyticsAccountRelationship;
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function normalizeEnvValue(value: string | undefined | null) {
  return value?.trim().toLowerCase() ?? '';
}

function isLocalHostname(hostname: string) {
  return LOCAL_HOSTNAMES.has(hostname) || hostname.endsWith('.local') || hostname.endsWith('.test');
}

function isPreviewHostname(hostname: string) {
  return hostname.includes('vercel.app') && hostname !== 'enterprise-staging.vercel.app';
}

function getAmplitudeEnvironment() {
  return normalizeEnvValue(import.meta.env.VITE_AMPLITUDE_ENVIRONMENT || import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE);
}

export function shouldEnableAmplitudeAnalytics({
  apiKey,
  explicitEnabled,
  environment,
  hostname,
}: {
  apiKey: string | undefined | null;
  explicitEnabled: string | undefined | null;
  environment: string | undefined | null;
  hostname: string;
}) {
  if (!apiKey?.trim()) {
    return false;
  }

  if (EXPLICIT_ENABLE_VALUES.has(normalizeEnvValue(explicitEnabled))) {
    return true;
  }

  if (isLocalHostname(hostname) || isPreviewHostname(hostname)) {
    return false;
  }

  return AMPLITUDE_ENABLED_ENVIRONMENTS.has(normalizeEnvValue(environment));
}

export function isAmplitudeAnalyticsEnabled() {
  if (!isBrowser()) {
    return false;
  }

  return shouldEnableAmplitudeAnalytics({
    apiKey: import.meta.env.VITE_AMPLITUDE_API_KEY,
    explicitEnabled: import.meta.env.VITE_AMPLITUDE_ENABLED,
    environment: getAmplitudeEnvironment(),
    hostname: window.location.hostname,
  });
}

export function initializeAmplitudeAnalytics() {
  if (!isBrowser()) {
    return null;
  }

  // ENT-3137 WP1 (VETO C1): public token-bearing routes (`/telelens/*`) carry a
  // bearer access token in the URL. Amplitude autocapture would exfiltrate that
  // URL on the first page view, so never boot Amplitude while on one of them.
  // Advisory detail routes also carry recipient-bound tracking tokens (ENT-5889).
  if (isPublicTokenRoute(window.location.pathname) || isAdvisoryCtaPath(window.location.pathname)) {
    return null;
  }

  if (!isAmplitudeAnalyticsEnabled()) {
    return null;
  }

  if (initialized) {
    return initPromise;
  }

  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY.trim();
  initialized = true;
  initPromise = amplitude.init(apiKey, AMPLITUDE_OPTIONS).promise.catch((error) => {
    initialized = false;
    initPromise = null;
    console.warn('Amplitude Analytics failed to initialize', error);
  });
  return initPromise;
}

function runWhenAmplitudeReady(operation: () => void) {
  const ready = initializeAmplitudeAnalytics();
  if (!ready) {
    return;
  }

  void ready.then(() => {
    if (initialized) {
      operation();
    }
  });
}

export type AmplitudeEventProperties = {
  'Signup Submitted': {
    'signup method': string;
    'email domain': string | null;
    'auth surface': string;
    'invited user': boolean;
  };
  'Account Created': {
    'signup method': string;
    'account id': string;
    'user role': string;
    'invited user': boolean;
    'plan selected': string | null;
  };
  'Email Verified': {
    'account id': string;
    'verification method': string;
    'time since account created seconds': number | null;
  };
  'Signed In': {
    'login method': string;
    'account id': string;
    'user role': string;
    'mfa enabled': boolean;
  };
  'Password Reset Requested': {
    'account id': string | null;
    'reset channel': string;
  };
  'Onboarding Completed': {
    'account id': string;
    'user role': string;
    'onboarding variant': string;
    'time since account created seconds': number | null;
  };
  'Supplier Added': {
    'account id': string;
    'supplier id': string;
    'add method': string;
    'supplier country': string | null;
    'supplier category': string | null;
  };
  'Supplier Invited': {
    'account id': string;
    'supplier id': string | null;
    'marketplace listing id': string;
    'supplier domain': string;
    'recipient domain': string | null;
    'invite channel': string;
    'entry point': string;
  };
  'Supplier Claim Started': {
    'account id': string | null;
    'supplier id': string | null;
    'marketplace listing id': string | null;
    'supplier domain': string | null;
    'claim source': string;
    'msp initiated': boolean;
  };
  'Supplier Claim Completed': {
    'account id': string | null;
    'supplier id': string | null;
    'marketplace listing id': string | null;
    'supplier domain': string | null;
    'claim source': string;
    'msp initiated': boolean;
  };
  'Suppliers Imported': {
    'account id': string;
    'import source': string;
    'suppliers imported count': number;
    'import duration seconds': number;
  };
  'Supplier Profile Viewed': {
    'account id': string;
    'supplier id': string;
    'entry point': string;
    'profile section': string;
  };
  'Supplier Assessment Completed': {
    'account id': string;
    'supplier id': string;
    'assessment type': string;
    'assessment status': string;
    'risk score band': string | null;
    'assessment duration seconds': number | null;
  };
  'Monitoring Enabled': {
    'account id': string;
    scope: string;
    'supplier id': string | null;
    'monitoring cadence': string | null;
    'signals enabled': string[];
  };
  'Certification Added': {
    'account id': string;
    'supplier id': string;
    'certification type': string;
    'certification issuer': string | null;
    'certification expiry date': string | null;
  };
  'Certification Verified': {
    'account id': string;
    'supplier id': string;
    'certification type': string;
    'verification method': string;
    'verification status': string;
  };
  'Questionnaire Sent': {
    'account id': string;
    'supplier id': string;
    'questionnaire template id': string;
    'delivery channel': string;
    'due date': string | null;
  };
  'Questionnaire Submitted': {
    'account id': string;
    'supplier id': string;
    'questionnaire template id': string;
    'submission status': string;
    'time since questionnaire sent seconds': number | null;
  };
  // SupplierPlus public external-share funnel (ENT-1610).
  // 'External Share Created' is the vendor-side adoption event. The remaining
  // four are the external recipient's (unauthenticated) conversion funnel:
  //   Opened -> Access Requested -> Access Granted -> Viewed.
  'External Share Created': {
    'account id': string | null;
    'vendor profile id': string | null;
    'marketplace listing id': string | null;
    'supplier domain': string | null;
    'share scope': string;
    'show supplierlens score': boolean;
  };
  'External Share Opened': {
    'vendor profile id': string | null;
    'supplier domain': string | null;
    'has viewer session': boolean;
  };
  'External Share Access Requested': {
    'vendor profile id': string | null;
    'supplier domain': string | null;
    'email domain': string | null;
  };
  'External Share Access Granted': {
    'vendor profile id': string | null;
    'supplier domain': string | null;
  };
  'External Share Viewed': {
    'vendor profile id': string | null;
    'supplier domain': string | null;
    'questionnaire count': number;
    'shows supplierlens score': boolean;
    'view source': string;
  };
  'Evidence Uploaded': {
    'account id': string;
    'supplier id': string;
    'evidence type': string;
    'file type': string | null;
    'file size mb': number | null;
  };
  'Remediation Plan Created': {
    'account id': string;
    'supplier id': string;
    'risk category': string;
    'plan priority': string;
    'due date': string | null;
  };
  'Remediation Task Completed': {
    'account id': string;
    'supplier id': string;
    'task type': string;
    'completion status': string;
    'time since plan created seconds': number | null;
  };
  'Alert Rule Created': {
    'account id': string;
    'alert type': string;
    'severity threshold': string | null;
    'delivery channels': string[];
    scope: string;
    'supplier id': string | null;
  };
  'Alert Acknowledged': {
    'account id': string;
    'alert id': string;
    'alert type': string;
    'alert severity': string | null;
    'supplier id': string | null;
    'time since alert created seconds': number | null;
  };
  'Report Exported': {
    'account id': string;
    'report type': string;
    'export format': string;
    'date range': string;
    'supplier count': number;
  };
  'Pitch Book PDF Export Started': {
    'account id': string | null;
    'organisation id': string | null;
    'marketplace listing id': string | null;
    'check id': string | null;
    'supplier domain': string;
    workflow: string;
    audience: string;
  };
  'Pitch Book PDF Export Completed': {
    'account id': string | null;
    'organisation id': string | null;
    'marketplace listing id': string | null;
    'check id': string | null;
    'supplier domain': string;
    workflow: string;
    audience: string;
    'job id': string;
    'request id': string | null;
    'file size mb': number | null;
    'duration seconds': number | null;
  };
  'Pitch Book PDF Export Failed': {
    'account id': string | null;
    'organisation id': string | null;
    'marketplace listing id': string | null;
    'check id': string | null;
    'supplier domain': string;
    workflow: string;
    audience: string;
    'job id': string | null;
    'request id': string | null;
    'error code': string | null;
    'http status code': number | null;
    'duration seconds': number | null;
  };
  'SupplierPlus Upgrade Viewed': {
    'account id': string | null;
    'supplier id': string | null;
    'billing interval': string;
    'billing path': string;
    'supplierplus access reason': string | null;
    'current plan slug': string | null;
    'subscription status': string | null;
    'user role': string | null;
  };
  'SupplierPlus Upgrade Started': {
    'account id': string | null;
    'supplier id': string | null;
    'billing interval': string;
    'billing path': string;
    'current plan slug': string | null;
    'subscription status': string | null;
    'user role': string | null;
  };
  'SupplierPlus Checkout Started': {
    'account id': string | null;
    'supplier id': string | null;
    'billing interval': string;
    'billing path': string;
    'current plan slug': string | null;
    'subscription status': string | null;
    'user role': string | null;
  };
  'SupplierPlus Access Active': {
    'account id': string | null;
    'supplier id': string | null;
    'supplierplus access reason': string | null;
    'current plan slug': string | null;
    'subscription status': string | null;
    'user role': string | null;
  };
  'Billing Checkout Started': {
    'account id': string | null;
    'plan selected': string | null;
    'billing interval': string | null;
    'billing path': string;
    'checkout source': string;
    'current plan slug': string | null;
    'subscription status': string | null;
  };
  'Payment Succeeded': {
    'account id': string | null;
    'plan selected': string | null;
    'billing interval': string | null;
    'billing type': string | null;
    'payment status source': string;
    'time since checkout started seconds': number | null;
  };
  'Payment Failed': {
    'account id': string | null;
    'plan selected': string | null;
    'billing interval': string | null;
    'billing type': string | null;
    'failure reason': string;
    'payment status source': string;
    'support notification status': string | null;
  };
  'Search Performed': {
    'account id': string;
    'search scope': string;
    'search query': string;
    'results count': number;
    'filters applied': string[];
  };
  'Error Encountered': {
    'error category': string;
    'error message': string;
    'error context': string;
    'http status code': number | null;
    provider: string | null;
    'account id': string | null;
    'supplier id': string | null;
  };
  'Page Viewed': {
    'page key': string;
    'route pattern': string;
    'previous page key': string | null;
    'previous route pattern': string | null;
    'navigation source': AnalyticsNavigationSource;
    'navigation surface': AnalyticsNavigationSurface;
  } & AnalyticsHierarchyProperties;
  'Navigation Performed': {
    'from page key': string;
    'to page key': string;
    'to route pattern': string;
    'navigation source': AnalyticsNavigationSource;
    'navigation surface': AnalyticsNavigationSurface;
    'navigation intent': string;
  } & AnalyticsHierarchyProperties;
  'Organisation Switched': {
    'from active org id': string | null;
    'from active org type': AnalyticsOrgType;
    'from account relationship': AnalyticsAccountRelationship;
    'to active org id': string | null;
    'to active org type': AnalyticsOrgType;
    'switch source': string;
  } & AnalyticsHierarchyProperties;
  'Component Viewed': {
    'component key': string;
    'page key': string;
    'feature area': string;
  } & AnalyticsHierarchyProperties;
};

export type AmplitudeEventName = keyof AmplitudeEventProperties;

export function trackAmplitudeEvent<EventName extends AmplitudeEventName>(
  eventName: EventName,
  properties: AmplitudeEventProperties[EventName],
) {
  if (!isAmplitudeAnalyticsEnabled()) {
    return;
  }

  const groups = { ...currentEventGroups };
  runWhenAmplitudeReady(() => {
    amplitude.track({
      event_type: eventName,
      event_properties: properties,
      groups,
    });
  });
}

export type AmplitudeUserProperties = {
  'User Role'?: string | null;
  'Account ID'?: string | null;
  'Account Type'?: string | null;
  'Subscription Tier'?: string | null;
  'Billing Cycle'?: string | null;
  'Signup Method'?: string | null;
  'MFA Enabled'?: boolean | null;
  'Onboarding Status'?: string | null;
  'Primary Compliance Framework'?: string | null;
};

export type AmplitudeGroupType = 'msp_org' | 'customer_org' | 'supplier_org';

export type AmplitudeGroupProperties = {
  'Org ID': string;
  'Org Type': AnalyticsOrgType;
  'Org Name': string | null;
};

export type AmplitudeGroupDefinition = {
  id: string;
  properties: AmplitudeGroupProperties;
};

export type AmplitudeGroups = Record<AmplitudeGroupType, AmplitudeGroupDefinition | null>;

export function identifyAmplitudeUser(properties: AmplitudeUserProperties) {
  if (!isAmplitudeAnalyticsEnabled()) {
    return;
  }

  const identify = new amplitude.Identify();

  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) {
      identify.set(key, value);
    }
  }

  runWhenAmplitudeReady(() => {
    amplitude.identify(identify);
  });
}

export function setAmplitudeGroups(groups: AmplitudeGroups) {
  currentEventGroups = Object.fromEntries(
    (Object.entries(groups) as Array<[AmplitudeGroupType, AmplitudeGroupDefinition | null]>)
      .filter(([, group]) => Boolean(group))
      .map(([groupType, group]) => [groupType, group?.id]),
  ) as Partial<Record<AmplitudeGroupType, string>>;

  if (!isAmplitudeAnalyticsEnabled()) {
    return;
  }

  runWhenAmplitudeReady(() => {
    for (const [groupType, group] of Object.entries(groups) as Array<[AmplitudeGroupType, AmplitudeGroupDefinition | null]>) {
      amplitude.setGroup(groupType, group ? [group.id] : []);

      if (!group) {
        continue;
      }

      const identify = new amplitude.Identify();
      for (const [key, value] of Object.entries(group.properties)) {
        identify.set(key, value);
      }
      if (group.properties['Org Name']) {
        identify.set('group_name', group.properties['Org Name']);
      }
      amplitude.groupIdentify(groupType, group.id, identify);
    }
  });
}

export function setAmplitudeUserId(userId: string | null | undefined) {
  if (!isAmplitudeAnalyticsEnabled()) {
    return;
  }

  runWhenAmplitudeReady(() => {
    amplitude.setUserId(userId || undefined);
  });
}

export function getSecondsSince(isoDate: string | null | undefined, now = Date.now()) {
  if (!isoDate) {
    return null;
  }

  const timestamp = Date.parse(isoDate);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return Math.max(0, Math.floor((now - timestamp) / 1000));
}

export function resetAmplitudeAnalyticsForTests() {
  initialized = false;
  initPromise = null;
  currentEventGroups = {};
}
